import React from 'react';

interface TestResult {
  passed: boolean;
  status?: 'passed' | 'failed' | 'not_required';
  details: string;
  qualifyingPeriods?: string[];
}

interface CausationTestProps {
  vTest: TestResult;
  modifiedVTest: TestResult;
  downOnlyTest: TestResult;
  benchmarkPeriod?: string;
}

export default function CausationTest({ vTest, modifiedVTest, downOnlyTest, benchmarkPeriod }: CausationTestProps) {
  // Determine if Modified V-Test should show as "NOT REQUIRED"
  // If V-Test passed, the backup test wasn't needed
  const getEffectiveStatus = (test: TestResult, isBackup: boolean, primaryPassed: boolean): 'passed' | 'failed' | 'not_required' => {
    if (test.status) return test.status;
    if (isBackup && primaryPassed && !test.passed) return 'not_required';
    return test.passed ? 'passed' : 'failed';
  };

  const modifiedVTestStatus = getEffectiveStatus(modifiedVTest, true, vTest.passed);

  const TestResultCard = ({ name, test, icon, status }: { name: string; test: TestResult; icon: string; status: 'passed' | 'failed' | 'not_required' }) => {
    const styles: Record<'passed' | 'failed' | 'not_required', { bg: React.CSSProperties; icon: string; text: string; badge: React.CSSProperties; label: string }> = {
      passed: {
        bg: { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' },
        icon: 'var(--color-verified)',
        text: 'var(--color-verified)',
        badge: { backgroundColor: 'transparent', color: 'var(--color-verified)' },
        label: 'PASSED'
      },
      failed: {
        bg: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
        icon: 'var(--color-needs-human-text)',
        text: 'var(--color-needs-human-text)',
        badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
        label: 'FAILED'
      },
      not_required: {
        bg: { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' },
        icon: 'var(--color-text-dim)',
        text: 'var(--color-text-secondary)',
        badge: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' },
        label: 'NOT REQUIRED'
      }
    };

    const style = styles[status];

    return (
      <div className="border-2 rounded-lg p-4" style={style.bg}>
        <div className="flex items-start gap-3">
          <div className="text-2xl" style={{ color: style.icon }}>{icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold" style={{ color: status === 'not_required' ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}>{name}</h4>
              <span className="px-2 py-1 rounded text-xs font-bold" style={style.badge}>
                {style.label}
              </span>
            </div>
            <p className="text-sm mb-2" style={{ color: style.text }}>
              {status === 'not_required' ? 'V-Test passed. Backup test not evaluated.' : test.details}
            </p>
            {status !== 'not_required' && test.qualifyingPeriods && test.qualifyingPeriods.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>Qualifying Periods:</p>
                <div className="flex flex-wrap gap-1">
                  {test.qualifyingPeriods.map((period: string, idx: number) => (
                    <span key={idx} className="px-2 py-1 rounded text-xs font-medium border" style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}>
                      {period}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Causation Analysis</h3>
        {benchmarkPeriod && (
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Benchmark Period: <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{benchmarkPeriod}</span>
          </p>
        )}
      </div>

      <div className="space-y-3">
        <TestResultCard name="V-Test (Standard)" test={vTest} icon="✓" status={vTest.passed ? 'passed' : 'failed'} />
        <TestResultCard name="Modified V-Test" test={modifiedVTest} icon="◇" status={modifiedVTestStatus} />
        <TestResultCard name="Down-Only Test" test={downOnlyTest} icon="↓" status={downOnlyTest.passed ? 'passed' : 'failed'} />
      </div>

      <div className="mt-6 p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>What This Means</h4>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          The business {vTest.passed ? 'passed' : 'failed'} the V-Test, which is the primary test for causation. 
          {vTest.passed && ' This confirms that revenue losses during the coverage period were significantly different from baseline patterns, supporting the claim for BI compensation.'}
          {!vTest.passed && ' Additional analysis may be needed to establish causation for the claim.'}
        </p>
      </div>
    </div>
  );
}
